import * as Consts from '@shared/core/consts';
import * as Models from '@shared/core/models';
import { Dates } from '@shared/core/utils/dates.utils';
import { PickupLabelGeneratorUtils } from './pickup-label-generator.utils';

interface PickupTimeParams {
    isSchedule: boolean;
    forDate: Date;
    orderTypeId: number;
    edgeDate?: Nullable<Date>;
    index?: Nullable<number>;
    isAsap?: boolean;
    displayFormat?: string;
    nextTick?: Nullable<number>;
    isDisplayAsTimespans?: boolean;
}

export class PickupsCreatorUtils {
    public static createPickupTimeObj(params: PickupTimeParams): OLO.Ordering.PickupTime {
        const {
            isSchedule,
            forDate,
            orderTypeId,
            edgeDate = null,
            index = null,
            isAsap = false,
            displayFormat = Consts.DATE_DISPLAY_FORMAT,
            nextTick = null,
            isDisplayAsTimespans = false,
        } = params;

        const baseData = PickupsCreatorUtils.getBaseTimeData(forDate, orderTypeId, edgeDate);
        const timeSpan = isDisplayAsTimespans ? PickupsCreatorUtils._calculateTimeSpan(forDate, nextTick) : null;
        const shortName = timeSpan ? PickupsCreatorUtils._formatTimeSpanShortName(timeSpan.startDate, timeSpan.endDate) : Dates.hoursFromDate(forDate);

        return new Models.PickupModels().generate({
            ...baseData,
            Index: index,
            Name: PickupLabelGeneratorUtils.createPickupLabelName({
                isScheduleOrderingEnabled: isSchedule,
                type: 'common',
                date: forDate,
                isAsap,
                format: displayFormat,
                asapMinutesFromNow: null,
                timeSpan,
            }),
            ShortName: shortName,
            IsAsap: isAsap,
            TimeSpan: timeSpan,
        });
    }

    public static overrdrivePickupTimeObjToAsap(timeObject: OLO.Ordering.PickupTime): OLO.Ordering.PickupTime {
        let pickupTimeObj = timeObject;

        if (pickupTimeObj.IsAsap) {
            const asapDate: Date = new Date(new Date().setSeconds(0, 0) + timeObject.MinutesFromNow * 60 * 1000);
            const baseData = PickupsCreatorUtils.getBaseTimeData(asapDate, timeObject.Id, timeObject.PlaceOrderTimeout);

            pickupTimeObj = new Models.PickupModels().generate({
                ...baseData,
                Index: -1,
                Name: PickupLabelGeneratorUtils.createPickupLabelName({
                    isScheduleOrderingEnabled: false,
                    type: 'common',
                    date: asapDate,
                    isAsap: true,
                    format: Consts.DATE_DISPLAY_FORMAT,
                    asapMinutesFromNow: null,
                    timeSpan: null,
                }),
                ShortName: `${Dates.hoursFromDate(asapDate, true)}`,
                MinutesFromNow: timeObject.MinutesFromNow,
                IsAsap: true,
                PlaceOrderTimeout: new Date(timeObject.PlaceOrderTimeout.getTime()),
            });
        }

        return pickupTimeObj;
    }

    public static getBaseTimeData(forDate: Date, orderTypeId: number, edgeDate: Date | null, meridiem: boolean = false, showSuffix: boolean = false) {
        const currentTime = new Date(new Date().setSeconds(0, 0));
        const dayNo = Dates.datesDiffInDays(forDate, currentTime);

        forDate.setSeconds(0, 0);

        return {
            Id: Number(`${orderTypeId}${forDate.getTime()}`),
            Date: new Date(forDate.getTime()),
            MinutesFromNow: Dates.datesDiffInMinutes(forDate, currentTime),
            Hour: `${Dates.hoursFromDate(forDate, meridiem, showSuffix)}:00`,
            DateLocalISO: Dates.getLocalISOFormatDate(forDate),
            PlaceOrderTimeout: edgeDate ? new Date(edgeDate.getTime()) : null,
            IsToday: dayNo === 0,
            DayNo: dayNo,
        };
    }

    private static _calculateTimeSpan(forDate: Date, nextTick: number) {
        const endDate = new Date(forDate.getTime() + nextTick * 60 * 1000);

        return { startDate: new Date(forDate.getTime()), endDate };
    }

    private static _formatTimeSpanShortName(startDate: Date, endDate: Date, isMeridiem = true, showSuffix = true) {
        return `${Dates.hoursFromDate(startDate, isMeridiem, showSuffix)} - ${Dates.hoursFromDate(endDate)}`;
    }
}
